import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { pdfjs, Document, Page } from "react-pdf";
import { Spin, Modal, Button } from "antd";
import { DownloadOutlined, ZoomInOutlined, ZoomOutOutlined, FullscreenOutlined } from "@ant-design/icons";

import backIcon from "../../assets/images/arrow-left.svg";
import LikeShareBookmark from "./components/LikeShareBookmark";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString();

const pdfViewerStyles = `
  .pdf-viewer-container {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-x pan-y;
    overflow: auto;
  }
  
  .pdf-content-wrapper {
    min-width: 100%;
    width: max-content;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;
  }
  
  .pdf-page {
    margin: 10px 0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    border-radius: 4px;
    transition: transform 0.2s ease;
    max-width: none;
  }
  
  .pdf-page:hover {
    transform: scale(1.01);
  }
  
  .zoom-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 8px;
    padding: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  .zoom-button {
    transition: all 0.2s ease;
  }
  
  .zoom-button:hover:not(:disabled) {
    background-color: #f0f0f0;
    transform: scale(1.05);
  }
  
  .zoom-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .react-pdf__Page {
    max-width: none !important;
    width: auto !important;
  }
  
  .react-pdf__Page__canvas {
    max-width: none !important;
    width: auto !important;
  }
`;

let lastScroll = 0;
export default function TatvaPediaDocument() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { contentData, url } = state != null && state

  const divRef = useRef<HTMLDivElement | null>(null);
  const [divWidth, setDivWidth] = useState(0);
  const [numPages, setNumPages] = useState<number>(0);
  const [printBlob, setPrintBlob] = useState<Blob | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [contentDetails, setContentDetails]: any = useState(null);
  const [fileType, setFileType] = useState<string>("");
  const [viewerError, setViewerError] = useState<string>("");
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [touchStartDistance, setTouchStartDistance] = useState(0);
  const [initialZoom, setInitialZoom] = useState(1);

  useEffect(() => {
    if (contentData) {
      setContentDetails(contentData);
      // Enhanced file type detection with multiple fallbacks
      const detectedFileType = detectFileType(url, contentData);
      setFileType(detectedFileType);
      console.log("Detected file type:", detectedFileType);
    }
  }, [contentData, url]);

  useEffect(() => {
    if (divRef.current) {
      setDivWidth(divRef.current.offsetWidth);
    }
  }, [divRef]);

  useEffect(() => {
    if (divRef.current) {
      const updateWidth = () => {
        setDivWidth(divRef.current?.offsetWidth || 0);
      };
      
      const timeoutId = setTimeout(updateWidth, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [zoomLevel]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      setTouchStartDistance(getDistance(e.touches));
      setInitialZoom(zoomLevel);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && touchStartDistance > 0) {
      e.preventDefault();
      const currentDistance = getDistance(e.touches);
      const scale = currentDistance / touchStartDistance;
      const newZoom = Math.max(0.5, Math.min(3, initialZoom * scale));
      setZoomLevel(newZoom);
    }
  };

  const handleTouchEnd = () => {
    setTouchStartDistance(0);
  };

  const pageWidth = useMemo(() => {
    const baseWidth = Math.max(divWidth, 375);
    return baseWidth * zoomLevel;
  }, [divWidth, zoomLevel]);

  // Comprehensive file type detection
  const detectFileType = useCallback((url: string, contentData: any) => {
    const urlWithoutParams = url?.split('?')[0] || "";
    const pathExtension = urlWithoutParams.split(".").pop()?.toLowerCase() || "";
    const contentType = contentData?.contentType?.toLowerCase() || "";
    const contentFormatId = contentData?.contentFormatId;
    const filename = contentData?.uploadContentUrl?.toLowerCase() || "";
    const filenameExtension = filename.split(".").pop() || "";
    if (pathExtension && ["pdf", "ppt", "pptx", "doc", "docx"].includes(pathExtension)) {
      return pathExtension;
    }
    if (filenameExtension && ["pdf", "ppt", "pptx", "doc", "docx"].includes(filenameExtension)) {
      return filenameExtension;
    }
    // Map content types to file extensions
    if (contentType.includes("pdf") || contentFormatId === 1001) return "pdf";
    if (contentType.includes("slide") || contentFormatId === 1003) return "pptx";
    if (contentType.includes("document") || contentFormatId === 1004) return "pdf";
    return pathExtension || filenameExtension || "";
  }, []);

  const onDocumentLoadSuccess = useCallback(async (successEvent: any) => {
    setNumPages(successEvent?.numPages);
    const data = await successEvent.getData();
    const blob: Blob = new Blob([data], { type: 'application/pdf' });
    setPrintBlob(blob);
  }, []);

  const handleScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const position = Math.ceil(
      (scrollTop / (scrollHeight - clientHeight)) * 100
    );

    if (position < lastScroll) {
      setIsVisible(true);
    } else if (position > lastScroll) {
      setIsVisible(false);
    }

    lastScroll = position;
  };

  // Render PDF content
  const renderPDFContent = useMemo(() => {
    return (
      <Document
        loading={<Spin style={{ position: 'absolute', zIndex: 0, left: "50%", top: "50%" }} />}
        error={<div style={{ position: 'absolute', zIndex: 0, left: "42%", top: "50%" }} >{'Failed to load PDF file.'}</div>}
        noData={<div style={{ position: 'absolute', zIndex: 0, left: "50%", top: "50%" }} >{'No PDF file specified.'}</div>}
        file={url}
        onLoadSuccess={onDocumentLoadSuccess}>
        {Array.apply(null, Array(numPages))
          .map((x, i) => i + 1)
          .map((page) => {
            return (
              <div key={`page-${page}`} className="pdf-page">
                <Page
                  loading={null}
                  width={pageWidth}
                  pageNumber={page}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>
            );
          })}
      </Document>
    );
  }, [numPages, divWidth, url, onDocumentLoadSuccess, pageWidth]);

  // Render PowerPoint content with multiple viewer options
  const renderPowerPointContent = useMemo(() => {
    const officeOnlineUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    return (
      <iframe
        src={officeOnlineUrl}
        width="100%"
        height="100%"
        frameBorder="0"
        title="PowerPoint Viewer"
        style={{ minHeight: 'calc(100vh - 57px)' }}
        onError={() => {
          const iframe = document.querySelector('iframe[title="PowerPoint Viewer"]') as HTMLIFrameElement;
          if (iframe) {
            iframe.src = googleDocsUrl;
          }
          setViewerError("Office Online viewer failed to load");
        }}
      />
    );
  }, [url]);
  const renderContent = () => {
    if (fileType === "pdf") {
      return renderPDFContent;
    } else if (fileType === "ppt" || fileType === "pptx") {
      return renderPowerPointContent;
    } else {
      return (
        <div style={{ 
          position: 'absolute', 
          zIndex: 0, 
          left: "50%", 
          top: "50%", 
          textAlign: 'center',
          transform: 'translate(-50%, -50%)',
          maxWidth: '400px'
        }}>
          <div style={{ 
            backgroundColor: '#f5f5f5',
            padding: '30px',
            borderRadius: '10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>
              Unsupported File Type
            </h3>
            <div style={{ marginBottom: '15px', color: '#666' }}>
              Detected: <strong>{fileType || 'Unknown'}</strong>
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '20px' }}>
              Supported types: PDF, PPT, PPTX
            </div>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={() => {
                const a = document.createElement('a');
                a.href = url;
                a.download = contentDetails?.uploadContentUrl || `document.${fileType}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }}
            >
              Download File
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <div className={isFullscreen ? "fixed inset-0 bg-white z-50" : ""}>
      <style>{pdfViewerStyles}</style>
      <div className="sticky top-0 bg-white z-10">
        <div className="flex gap-1.5 items-center px-3 py-4 text-base font-semibold tracking-tight text-center bg-white border-b border-solid border-b-zinc-200 text-[#1F2933]">
          <img
            loading="lazy"
            src={backIcon}
            className="object-contain shrink-0 self-stretch my-auto w-6 aspect-square cursor-pointer"
            onClick={() => navigate(-1)}
          />
          <span className="flex-1 truncate">{contentDetails?.uploadContentUrl}</span>
          
          {/* Zoom Controls - Only show for PDF files */}
          {fileType === "pdf" && (
            <div className="zoom-controls">
              <button
                onClick={handleZoomOut}
                disabled={zoomLevel <= 0.5}
                className="zoom-button p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom Out"
              >
                <ZoomOutOutlined />
              </button>
              <span className="text-sm text-gray-600 min-w-[60px] text-center">
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={zoomLevel >= 3}
                className="zoom-button p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom In"
              >
                <ZoomInOutlined />
              </button>
              <button
                onClick={handleZoomReset}
                className="zoom-button px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                title="Reset Zoom"
              >
                Reset
              </button>
              {/* <button
                onClick={handleFullscreen}
                className="zoom-button p-2 rounded-lg hover:bg-gray-100"
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                <FullscreenOutlined />
              </button> */}
            </div>
          )}
        </div>
      </div>
      <div 
        ref={divRef} 
        className="pdf-viewer-container" 
        style={{ 
          // height: isFullscreen ? 'calc(100vh - 57px)' : 'calc(100vh - 57px)', 
          height: 'calc(100vh - 80px)',
          scrollbarWidth: 'none'
        }} 
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="pdf-content-wrapper">
          {renderContent()}
        </div>
        {printBlob && fileType === "pdf" && (
          <LikeShareBookmark
            contentDetails={contentDetails}
            setContentDetails={setContentDetails}
            isVisible={isVisible}
          />
        )}
      </div>
      
      {/* Error Modal */}
      <Modal
        title="Viewer Error"
        open={!!viewerError}
        onOk={() => setViewerError("")}
        onCancel={() => setViewerError("")}
        footer={[
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={() => {
            const a = document.createElement('a');
            a.href = url;
            a.download = contentDetails?.uploadContentUrl || `document.${fileType}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }}>
            Download Instead
          </Button>,
          <Button key="close" onClick={() => setViewerError("")}>
            Close
          </Button>
        ]}
      >
        <p>{viewerError}</p>
        <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
          You can try downloading the file to view it locally.
        </p>
      </Modal>
    </div>
  );
}
